import { Group, Input, NumberInput, Select, Stack, TagsInput, Text } from "@mantine/core";
import dayjs from "dayjs";
import { useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import { useIpawsAlertFormAllowedEventCodes } from "shared/components/ipaws/alert/query";
import { useFormState } from "shared/hooks/useFormState";
import { some } from "shared/utils/maybe";
import { IpawsChannel } from "src/gql/graphql";
import { match } from "ts-pattern";
import z from "zod";

export const IpawsAlertFormConfiguration = () => {
    const form = useIpawsAlertFormContext();
    const [duration, setDuration] = useFormState(form, "durationMinutes");
    const [channels] = useFormState(form, "channels");

    const { data: eventCodes } = useIpawsAlertFormAllowedEventCodes();

    function getDurationHrsValue() {
        return Math.floor(duration / 60);
    }

    function getDurationMinsValue() {
        return duration % 60;
    }

    function setDurationValue(n: number, unit: "minutes" | "hours") {
        match(unit)
            .with("minutes", () => setDuration(getDurationHrsValue() * 60 + n))
            .with("hours", () => setDuration(n * 60 + getDurationMinsValue()))
            .exhaustive();
    }

    return (
        <IpawsAlertFormStepLayout>
            <Stack>
                <Select
                    label="Event Code"
                    required
                    searchable
                    {...form.getInputProps("eventCode")}
                    data={eventCodes?.map((c) => ({ value: c.id, label: `${c.id}: ${c.description}` }))}
                />
                <Input.Wrapper required label="Duration" error={form.getInputProps("durationMinutes").error}>
                    <Stack>
                        <Group>
                            <Text size="sm">Expire after: </Text>
                            <NumberInput
                                aria-label="Duration in hours"
                                min={0}
                                value={getDurationHrsValue()}
                                onChange={(v) =>
                                    some(v)
                                        .map(z.coerce.number().parse)
                                        .take((n) => setDurationValue(n, "hours"))
                                }
                                suffix=" hours"
                            />
                            <NumberInput
                                aria-label="Duration in minutes"
                                min={0}
                                max={60}
                                value={getDurationMinsValue()}
                                onChange={(v) =>
                                    some(v)
                                        .map(z.coerce.number().parse)
                                        .take((n) => setDurationValue(n, "minutes"))
                                }
                                suffix=" minutes"
                            />
                        </Group>
                        <Input.Error />
                    </Stack>
                </Input.Wrapper>
                <Text size="xs" c="dimmed">
                    Estimated expiration: {dayjs().add(duration, "minutes").toDate().toLocaleString()} (local)
                </Text>
                {channels.includes(IpawsChannel.Capexch) && (
                    <TagsInput label="Send to COG(s)" required {...form.getInputProps("cogRecipients")} />
                )}
            </Stack>
        </IpawsAlertFormStepLayout>
    );
};
